import type { ReactNode } from 'react';
import { Dialog as D } from 'radix-ui';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { overlayCls } from './Dialog';

export interface SheetProps {
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  trigger?: ReactNode;
  side?: 'left' | 'right' | 'bottom';
  title: ReactNode;
  /** Hide the visual header (title stays for screen readers) — e.g. the mobile nav drawer. */
  bare?: boolean;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  closeLabel?: string;
}

const sides = {
  left: 'inset-y-0 left-0 h-full border-r data-[state=open]:animate-[gh-slide-left_260ms_var(--gh-ease-out)]',
  right:
    'inset-y-0 right-0 h-full border-l data-[state=open]:animate-[gh-slide-right_260ms_var(--gh-ease-out)]',
  bottom:
    'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl border-t data-[state=open]:animate-[gh-slide-up_260ms_var(--gh-ease-out)]',
};

/** Side panel / drawer (detail panels, filters, mobile navigation). */
export function Sheet({
  open,
  onOpenChange,
  trigger,
  side = 'right',
  title,
  bare,
  description,
  children,
  footer,
  width = 'w-[min(28rem,calc(100vw-2.5rem))]',
  closeLabel = 'Κλείσιμο',
}: SheetProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <D.Trigger asChild>{trigger}</D.Trigger>}
      <D.Portal>
        <D.Overlay className={overlayCls} />
        <D.Content
          className={cn(
            'fixed z-50 flex flex-col border-hairline bg-surface shadow-lg focus:outline-none',
            sides[side],
            side !== 'bottom' && width,
          )}
        >
          {bare ? (
            <>
              <D.Title className="sr-only">{title}</D.Title>
              <D.Description className="sr-only">{description ?? ''}</D.Description>
            </>
          ) : (
            <div className="flex items-start justify-between gap-4 border-b border-hairline px-5 py-4">
              <div className="grid gap-0.5">
                <D.Title className="font-display-tight text-lg">{title}</D.Title>
                {description ? (
                  <D.Description className="text-sm text-ink-muted">{description}</D.Description>
                ) : (
                  <D.Description className="sr-only">{''}</D.Description>
                )}
              </div>
              <D.Close
                aria-label={closeLabel}
                className="rounded-sm p-1.5 text-ink-subtle hover:bg-sunken hover:text-ink focus-visible:outline-2 focus-visible:outline-focus"
              >
                <XIcon className="size-[18px]" />
              </D.Close>
            </div>
          )}
          <div className={cn('min-h-0 flex-1 overflow-y-auto', !bare && 'px-5 py-4')}>
            {children}
          </div>
          {footer && (
            <div className="flex justify-end gap-2 border-t border-hairline bg-canvas/70 px-5 py-3">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
